// The weekly recap (RECAP.md): the period's facts in, a headline, a
// standfirst, and short sections out, every clause backed by a number.
// Variants are chosen by hash so two leagues never read the same. No
// clocks, no I/O, no sport names, no model call.

import {
  gamesOf,
  hashString,
  formatValue,
  type MatchupFact,
  type PeriodFacts,
  type SeasonFacts,
  type TeamPeriodFact,
} from '@fsp/awards';
import type { NextPeriod, Recap, RecapInput, RecapLine, RecapSection } from './types';

// ── Helpers ───────────────────────────────────────────────────────────────

function pts(n: number): string {
  return formatValue(n, 'points');
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

function fill(template: string, d: Record<string, string | number>): string {
  return template
    .replace(/\{(\w+)\}/g, (_, k: string) => (d[k] === undefined ? '' : String(d[k])))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** A stable variant per league, period, and slot. */
function variant<T>(facts: SeasonFacts, period: number, slot: string, options: readonly T[]): T {
  return options[
    hashString(`${facts.leagueId}:${facts.season}:${period}:${slot}`) % options.length
  ]!;
}

/**
 * `variant`, passing over the options this recap has already used, from the
 * one the hash lands on: two blowouts in a week read "That's not a game,
 * that's a statement." twice (B-394). The hash's own pick where every option
 * is used, so a busy week still has a line for each game.
 */
function freshVariant(
  facts: SeasonFacts,
  period: number,
  slot: string,
  options: readonly string[],
  used: Set<string>,
): string {
  const start = hashString(`${facts.leagueId}:${facts.season}:${period}:${slot}`) % options.length;
  for (let i = 0; i < options.length; i++) {
    const option = options[(start + i) % options.length]!;
    if (!used.has(option)) {
      used.add(option);
      return option;
    }
  }
  return options[start]!;
}

function owner(facts: SeasonFacts, teamId: string): string {
  return facts.teams.find((t) => t.teamId === teamId)?.ownerName ?? 'somebody';
}

function line(
  text: string,
  teamId: string | null = null,
  matchupId: string | null = null,
  emphasis = false,
): RecapLine {
  return { text, teamId, matchupId, emphasis, names: [] };
}

/**
 * THE NAMES IN A LINE, found after it is written (B-220): every manager in
 * the league and every player the week could mention — each team's top
 * starter and every player who changed hands — wherever the text says them
 * as a whole word. Found rather than threaded through each template, so a
 * template added later is linked without anyone remembering to. A name two
 * managers share is left as words: it cannot say which team it means.
 */
function namesIn(text: string, candidates: readonly Candidate[]): RecapLine['names'] {
  const taken: RecapLine['names'] = [];
  const word = /[\p{L}\p{N}]/u;
  // Longest first, so "Rico Jr." is not claimed by "Rico".
  for (const c of [...candidates].sort((a, b) => b.text.length - a.text.length)) {
    if (c.text === '') continue;
    let from = 0;
    for (;;) {
      const start = text.indexOf(c.text, from);
      if (start < 0) break;
      const end = start + c.text.length;
      from = end;
      if (word.test(text[start - 1] ?? '') || word.test(text[end] ?? '')) continue;
      if (taken.some((t) => start < t.end && end > t.start)) continue;
      taken.push({ kind: c.kind, id: c.id, start, end });
    }
  }
  return taken.sort((a, b) => a.start - b.start);
}

interface Candidate {
  kind: 'player' | 'team';
  id: string;
  text: string;
}

function candidatesFor(facts: SeasonFacts, p: PeriodFacts): Candidate[] {
  const owners = new Map<string, string[]>();
  for (const t of facts.teams)
    owners.set(t.ownerName, [...(owners.get(t.ownerName) ?? []), t.teamId]);
  const out: Candidate[] = [];
  for (const [text, ids] of owners)
    if (ids.length === 1) out.push({ kind: 'team', id: ids[0]!, text });
  const players = new Set<string>();
  for (const t of p.teams) if (t.topStarter) players.add(t.topStarter.playerId);
  for (const tx of p.transactions) players.add(tx.playerId);
  const byName = new Map<string, string[]>();
  for (const id of players) {
    const name = facts.playerName(id);
    byName.set(name, [...(byName.get(name) ?? []), id]);
  }
  for (const [text, ids] of byName)
    if (ids.length === 1) out.push({ kind: 'player', id: ids[0]!, text });
  return out;
}

interface StandingRow {
  teamId: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  rank: number;
}

/**
 * A league whose table is a race on points (total points, elimination): no
 * record, ranked by points (PLAN-2026-10-16, B-361).
 */
export function racesOnPoints(facts: SeasonFacts): boolean {
  return facts.format === 'points-total' || facts.format === 'elimination';
}

/**
 * Regular-season standings through a period, ranked like the standings screen
 * (wins, then points; points alone in a race). A week against the field (Q4,
 * Q5) has no result: its points count, and in all-play its record is the
 * team's score against every other team's.
 */
export function standingsThrough(facts: SeasonFacts, period: number): StandingRow[] {
  const rows = new Map<string, StandingRow>();
  for (const t of facts.teams)
    rows.set(t.teamId, { teamId: t.teamId, wins: 0, losses: 0, ties: 0, pointsFor: 0, rank: 0 });
  for (const p of facts.periods) {
    if (!p.final || p.period > period || p.period > facts.regularSeasonEnd) continue;
    const field = p.teams.filter((t) => t.played && t.result === null && t.opponentTeamId === null);
    for (const t of field) {
      const row = rows.get(t.teamId);
      if (!row) continue;
      row.pointsFor += t.score;
      if (facts.format !== 'all-play') continue;
      for (const o of field) {
        if (o.teamId === t.teamId) continue;
        if (t.score > o.score) row.wins++;
        else if (t.score < o.score) row.losses++;
        else row.ties++;
      }
    }
    for (const t of p.teams) {
      const row = rows.get(t.teamId);
      if (!row || !t.played || t.result === null) continue;
      // A week's score once, and each of its games' results (Q6).
      row.pointsFor += t.score;
      for (const g of gamesOf(t)) {
        if (g.result === 'win') row.wins++;
        else if (g.result === 'loss') row.losses++;
        else row.ties++;
      }
    }
  }
  const race = racesOnPoints(facts);
  const out = [...rows.values()].sort((a, b) =>
    race ? b.pointsFor - a.pointsFor : b.wins - a.wins || b.pointsFor - a.pointsFor,
  );
  out.forEach((r, i) => (r.rank = i + 1));
  return out;
}

function record(row: StandingRow | undefined): string {
  if (!row) return '0–0';
  return `${row.wins}–${row.losses}${row.ties ? `–${row.ties}` : ''}`;
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

// ── Games ─────────────────────────────────────────────────────────────────

type Story = 'tie' | 'tiebreak' | 'upset' | 'nailbiter' | 'blowout' | 'heartbreak' | 'ordinary';

/**
 * How a tie was settled, in a sentence's few words. The same words as
 * `settledOn` in `@fsp/domain`, which the matchup and the notifications use;
 * a copy here because this package depends on the awards engine alone.
 */
function settledOn(how: string | null): string {
  switch (how) {
    case 'bench-points':
      return 'on bench points';
    case 'coin-flip':
      return 'on a coin flip';
    case 'home-team':
      return 'on home field';
    case 'higher-seed':
      return 'on seeding';
    default:
      return 'on the tiebreaker';
  }
}

/**
 * Who a final went to, and by what rule (N1, B-232): as the scorer settled it
 * where the facts say, else by the scores, a level bracket game going to the
 * home side, the higher seed. A tie the league broke is a win and a loss, not
 * a tie — the recap called it "tied" and Home said "You lost by 0.0" until the
 * review of N1.
 */
function settled(m: MatchupFact): { winnerId: string | null; how: string | null } {
  if (m.winnerTeamId !== undefined) return { winnerId: m.winnerTeamId, how: m.decidedBy ?? null };
  if (m.homeScore > m.awayScore) return { winnerId: m.homeTeamId, how: null };
  if (m.awayScore > m.homeScore) return { winnerId: m.awayTeamId, how: null };
  return m.kind === 'regular'
    ? { winnerId: null, how: null }
    : { winnerId: m.homeTeamId, how: 'higher-seed' };
}

interface Game {
  m: MatchupFact;
  winnerId: string;
  loserId: string;
  w: number;
  l: number;
  margin: number;
  story: Story;
  weight: number;
  /** The rule that settled a level score, for a `tiebreak` story. */
  how: string | null;
}

function classify(p: PeriodFacts, before: StandingRow[]): Game[] {
  const scores = p.teams.filter((t) => t.played).map((t) => t.score);
  const mid = median(scores);
  const rec = (id: string) => before.find((r) => r.teamId === id);
  const games: Game[] = [];
  for (const m of p.matchups) {
    if (!m.final) continue;
    const result = settled(m);
    // A tie has no winner; its two names go home first, as they always did.
    const homeWon = result.winnerId === null || result.winnerId === m.homeTeamId;
    const winnerId = homeWon ? m.homeTeamId : m.awayTeamId;
    const loserId = homeWon ? m.awayTeamId : m.homeTeamId;
    const w = Math.max(m.homeScore, m.awayScore);
    const l = Math.min(m.homeScore, m.awayScore);
    const margin = Math.round((w - l) * 10) / 10;
    let story: Story = 'ordinary';
    let weight = 1;
    if (m.homeScore === m.awayScore) {
      story = result.winnerId === null ? 'tie' : 'tiebreak';
      weight = 3;
    } else {
      const wr = rec(winnerId);
      const lr = rec(loserId);
      const upset = !!wr && !!lr && lr.wins - wr.wins >= 2 && m.kind === 'regular';
      if (upset) {
        story = 'upset';
        weight = 4 + (margin < 3 ? 1 : 0);
      } else if (margin < 3) {
        story = 'nailbiter';
        weight = 3;
      } else if (margin >= 40) {
        story = 'blowout';
        weight = 3;
      } else if (l >= mid && scores.length >= 4) {
        story = 'heartbreak';
        weight = 2;
      }
    }
    games.push({ m, winnerId, loserId, w, l, margin, story, weight, how: result.how });
  }
  return games.sort((a, b) => b.weight - a.weight || b.margin - a.margin);
}

const GAME_LINES: Record<Story, string[]> = {
  tie: ['{wOwner} and {lOwner} tied at {w}. Nobody knows what to do with that.'],
  tiebreak: ['{wOwner} and {lOwner} tied at {w}, and {wOwner} took it {how}.'],
  upset: [
    '{wOwner} ({wRecord}) takes down {lOwner} ({lRecord}), {w}–{l}. Nobody saw it, including {wOwner}.',
    'Upset: {wOwner} over {lOwner}, {w}–{l}. {lOwner} came in {lRecord} and leaves with questions.',
  ],
  nailbiter: [
    '{wOwner} edges {lOwner}, {w}–{l}. One catch either way.',
    '{w}–{l}, {wOwner} over {lOwner}, decided by {margin}. Every point mattered, for once.',
    '{wOwner} survives {lOwner} by {margin}. Somebody is refreshing the stat corrections.',
  ],
  blowout: [
    "{wOwner} flattens {lOwner}, {w}–{l}. That's not a game, that's a statement.",
    '{wOwner} over {lOwner} by {margin}. {lOwner} has been asked to stop checking the score.',
    '{wOwner} {w}, {lOwner} {l}. The margin was {margin}. The mood was worse.',
  ],
  heartbreak: [
    '{lOwner} scored {l} and lost. {wOwner} needed every one of {w}.',
    '{w}–{l}: {lOwner} put up a winning score against the one team that could beat it.',
  ],
  ordinary: [
    '{wOwner} beats {lOwner}, {w}–{l}.',
    '{wOwner} over {lOwner}, {w}–{l}. {wTop} led the way with {wTopPts}.',
    '{wOwner} handles {lOwner}, {w}–{l}.',
  ],
};

function gameLine(
  facts: SeasonFacts,
  p: PeriodFacts,
  g: Game,
  before: StandingRow[],
  /** The templates this week's games have used: each game its own where there is one. */
  used: Set<string>,
): RecapLine {
  const wt = p.teams.find((t) => t.teamId === g.winnerId);
  const top = wt?.topStarter ? facts.playerName(wt.topStarter.playerId) : '';
  const templates = GAME_LINES[g.story];
  // The "led the way" variant needs a top starter to name.
  const usable = top ? templates : templates.filter((t) => !t.includes('{wTop}'));
  const text = fill(freshVariant(facts, p.period, `game:${g.m.id}`, usable, used), {
    wOwner: owner(facts, g.winnerId),
    lOwner: owner(facts, g.loserId),
    w: pts(g.w),
    l: pts(g.l),
    margin: pts(g.margin),
    wRecord: record(before.find((r) => r.teamId === g.winnerId)),
    lRecord: record(before.find((r) => r.teamId === g.loserId)),
    wTop: top,
    wTopPts: wt?.topStarter ? pts(wt.topStarter.points) : '',
    how: settledOn(g.how),
  });
  return line(text, g.winnerId, g.m.id, g.weight >= 3);
}

// ── Headline ──────────────────────────────────────────────────────────────

const HEADLINES = {
  bestWeek: [
    '{owner} posts the best week of the season{sofar}: {value}.',
    '{value}. {owner} just set the bar for the year{sofar}.',
  ],
  seasonLow: [
    '{owner} sets the season low: {value}. Moment of silence.',
    "{value} points. {owner}'s week will be studied.",
  ],
  upset: [
    '{wOwner} stuns {lOwner}, {w}–{l}.',
    'Upset of the week: {wOwner} over {lOwner}, {w}–{l}.',
    "{lOwner} was {lRecord}. {wOwner} didn't care: {w}–{l}.",
  ],
  nailbiter: [
    '{wOwner} survives {lOwner} by {margin}.',
    "{margin} points. That's the whole story of {wOwner} over {lOwner}.",
    '{wOwner} edges {lOwner}, {w}–{l}. Somebody check the stat corrections.',
  ],
  blowout: [
    '{wOwner} runs {lOwner} out of the building, {w}–{l}.',
    '{wOwner} {w}, {lOwner} {l}. The margin was {margin}. The mood was worse.',
    '{wOwner} by {margin}. {lOwner} has been asked not to check the score.',
  ],
  tie: ['{wOwner} and {lOwner} tie at {w}. The league has no rule for this feeling.'],
  tiebreak: ['{wOwner} and {lOwner} tie at {w}, and {wOwner} takes it {how}.'],
  high: [
    '{owner} leads the week with {value}.',
    '{value} for {owner}, the most in the league this week.',
  ],
};

const STANDFIRSTS = [
  '{games} games, {close} decided by under five, and {highOwner} on top with {high}.',
  "{highOwner}'s {high} was the week's best; {close} of {games} games came down to the last game.",
  '{games} games. {closeOnes}. {highOwner} scored {high} and would like everyone to know.',
];
/** A championship, or any week with one game in it: no count to say. */
const STANDFIRSTS_ONE = ['One game this week, and {highOwner} took it with {high}.'];
/** A week against the field (Q4, Q5): no games to count. */
const STANDFIRSTS_FIELD = [
  'Everybody against everybody, and {highOwner} on top with {high}.',
  "{highOwner}'s {high} topped the whole league this week.",
];
const STANDFIRSTS_NO_CLOSE = [
  '{games} games, none of them close, and {highOwner} on top with {high}.',
  'Nobody sweated this week: {games} games, no nail-biters, {highOwner} on top with {high}.',
];

function headline(
  facts: SeasonFacts,
  p: PeriodFacts,
  games: Game[],
  before: StandingRow[],
): { headline: string; standfirst: string } {
  const played = p.teams.filter((t) => t.played);
  const high = played.reduce<TeamPeriodFact | null>(
    (b, t) => (!b || t.score > b.score ? t : b),
    null,
  );
  const low = played.reduce<TeamPeriodFact | null>(
    (b, t) => (!b || t.score < b.score ? t : b),
    null,
  );
  const allScores = facts.periods
    .filter((x) => x.final)
    .flatMap((x) => x.teams.filter((t) => t.played).map((t) => t.score));
  const seasonHigh = allScores.length ? Math.max(...allScores) : 0;
  const seasonLow = allScores.length ? Math.min(...allScores) : 0;
  const top = games[0] ?? null;
  const candidates: { weight: number; text: string }[] = [];
  const gameDetail = (g: Game) => ({
    wOwner: owner(facts, g.winnerId),
    lOwner: owner(facts, g.loserId),
    w: pts(g.w),
    l: pts(g.l),
    margin: pts(g.margin),
    lRecord: record(before.find((r) => r.teamId === g.loserId)),
    how: settledOn(g.how),
  });
  // A close upset beats "best week so far"; a plain upset ties it, and the
  // record breaks the tie because it was pushed first.
  if (high && high.score >= seasonHigh && facts.periods.filter((x) => x.final).length > 1)
    candidates.push({
      weight: 4,
      text: fill(variant(facts, p.period, 'h:best', HEADLINES.bestWeek), {
        owner: owner(facts, high.teamId),
        value: pts(high.score),
        // Early in the year it is only the best week so far.
        sofar:
          facts.periods.filter((x) => x.final).length * 2 < facts.regularSeasonEnd ? ' so far' : '',
      }),
    });
  if (low && low.score <= seasonLow && facts.periods.filter((x) => x.final).length > 1)
    candidates.push({
      weight: 3,
      text: fill(variant(facts, p.period, 'h:low', HEADLINES.seasonLow), {
        owner: owner(facts, low.teamId),
        value: pts(low.score),
      }),
    });
  if (top && top.story !== 'ordinary' && top.story !== 'heartbreak')
    candidates.push({
      weight: top.weight,
      text: fill(variant(facts, p.period, `h:${top.story}`, HEADLINES[top.story]), gameDetail(top)),
    });
  if (high)
    candidates.push({
      weight: 1,
      text: fill(variant(facts, p.period, 'h:high', HEADLINES.high), {
        owner: owner(facts, high.teamId),
        value: pts(high.score),
      }),
    });
  candidates.sort((a, b) => b.weight - a.weight);
  const close = games.filter((g) => g.story !== 'tie' && g.margin < 5).length;
  const standfirst = high
    ? fill(
        variant(
          facts,
          p.period,
          'standfirst',
          games.length === 0
            ? STANDFIRSTS_FIELD
            : games.length === 1
              ? STANDFIRSTS_ONE
              : close === 0
                ? STANDFIRSTS_NO_CLOSE
                : STANDFIRSTS,
        ),
        {
          games: games.length,
          close,
          closeOnes: close === 1 ? 'One close one' : `${close} close ones`,
          highOwner: owner(facts, high.teamId),
          high: pts(high.score),
        },
      )
    : '';
  return { headline: candidates[0]?.text ?? `Week ${p.period}.`, standfirst };
}

// ── Sections ──────────────────────────────────────────────────────────────

function stars(facts: SeasonFacts, p: PeriodFacts): RecapSection {
  const top = p.teams
    .filter((t) => t.played && t.topStarter)
    .map((t) => ({
      teamId: t.teamId,
      player: facts.playerName(t.topStarter!.playerId),
      points: t.topStarter!.points,
    }))
    .sort((a, b) => b.points - a.points);
  const lines: RecapLine[] = [];
  const first = top[0];
  if (first)
    lines.push(
      line(
        fill(
          variant(facts, p.period, 'star', [
            'Player of the week: {player} ({owner}), {points}.',
            '{player} scored {points} for {owner}. Nobody else came close.',
          ]),
          { player: first.player, owner: owner(facts, first.teamId), points: pts(first.points) },
        ),
        first.teamId,
        null,
        true,
      ),
    );
  const rest = top.slice(1, 3);
  if (rest.length)
    lines.push(
      line(
        `Also: ${rest.map((r) => `${r.player} (${owner(facts, r.teamId)}) ${pts(r.points)}`).join(', ')}.`,
      ),
    );
  return { id: 'stars', title: 'The stars', lines };
}

function bench(facts: SeasonFacts, p: PeriodFacts): RecapSection {
  // Only the benches somebody wrote down; a week with none known says nothing
  // about the bench rather than that nobody left much on it.
  const known = p.teams.flatMap((t) =>
    t.played && t.optimalPoints !== null
      ? [{ t, optimal: t.optimalPoints, bench: t.benchPoints ?? 0 }]
      : [],
  );
  const worst = known
    .map((k) => ({ ...k, left: Math.round((k.optimal - k.t.score) * 10) / 10 }))
    .sort((a, b) => b.left - a.left)[0];
  const lines: RecapLine[] = [];
  if (known.length === 0) return { id: 'bench', title: 'The bench', lines };
  if (!worst || worst.left < 5)
    lines.push(line('Nobody left much on the bench this week. Suspicious.'));
  else
    lines.push(
      line(
        fill(
          variant(facts, p.period, 'bench', [
            '{owner} could have had {optimal} instead of {score}. The bench scored {bench}; some of it should have started.',
            'Left on the bench: {left} by {owner}. The right players were on the roster, just not in the lineup.',
            "{owner}'s best lineup was worth {optimal}. The one that played scored {score}. That's {left} of regret.",
          ]),
          {
            owner: owner(facts, worst.t.teamId),
            optimal: pts(worst.optimal),
            score: pts(worst.t.score),
            bench: pts(worst.bench),
            left: pts(worst.left),
          },
        ),
        worst.t.teamId,
      ),
    );
  return { id: 'bench', title: 'The bench', lines };
}

function standings(
  facts: SeasonFacts,
  p: PeriodFacts,
  games: Game[],
  before: StandingRow[],
  after: StandingRow[],
  playoffTeamCount: number,
): RecapSection {
  const lines: RecapLine[] = [];
  if (p.period > facts.regularSeasonEnd) {
    for (const g of games.filter((x) => x.m.kind === 'playoff' && x.story !== 'tie'))
      lines.push(
        line(
          `${owner(facts, g.winnerId)} advances; ${owner(facts, g.loserId)} is done.`,
          g.winnerId,
          g.m.id,
        ),
      );
    if (!lines.length) lines.push(line('The bracket waits for the last game.'));
    return { id: 'standings', title: 'The playoffs', lines };
  }
  const moves = after
    .map((r) => {
      const was = before.find((b) => b.teamId === r.teamId)?.rank ?? r.rank;
      return { r, delta: was - r.rank };
    })
    .filter((x) => x.delta !== 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const climber = moves.find((x) => x.delta > 0);
  const faller = moves.find((x) => x.delta < 0);
  if (climber)
    lines.push(
      line(
        `${owner(facts, climber.r.teamId)} climbs ${climber.delta === 1 ? 'a spot' : `${climber.delta} spots`} to ${ordinal(climber.r.rank)}.`,
        climber.r.teamId,
      ),
    );
  if (faller)
    lines.push(
      line(
        `${owner(facts, faller.r.teamId)} drops ${-faller.delta === 1 ? 'a spot' : `${-faller.delta} spots`} to ${ordinal(faller.r.rank)}.`,
        faller.r.teamId,
      ),
    );
  const leader = after[0];
  if (leader)
    lines.push(
      line(
        racesOnPoints(facts)
          ? `${owner(facts, leader.teamId)} leads with ${pts(leader.pointsFor)}.`
          : `${owner(facts, leader.teamId)} leads at ${record(leader)}.`,
        leader.teamId,
      ),
    );
  const last = after[playoffTeamCount - 1];
  const first = after[playoffTeamCount];
  if (last && first) {
    const gap = last.wins - first.wins;
    lines.push(
      line(
        gap === 0
          ? `${owner(facts, last.teamId)} holds the last playoff spot on points; ${owner(facts, first.teamId)} is level on wins.`
          : `${owner(facts, last.teamId)} holds the last playoff spot; ${owner(facts, first.teamId)} is ${gap} ${gap === 1 ? 'game' : 'games'} back.`,
        last.teamId,
      ),
    );
  }
  return { id: 'standings', title: 'The standings', lines };
}

function wire(facts: SeasonFacts, p: PeriodFacts): RecapSection {
  const best = [...p.transactions]
    .filter((tx) => tx.kind !== 'trade' && tx.pointsThisPeriod > 0)
    .sort((a, b) => b.pointsThisPeriod - a.pointsThisPeriod)[0];
  const lines = best
    ? [
        line(
          fill(
            variant(facts, p.period, 'wire', [
              'Pickup of the week: {player}, {points} for {owner} the week he was added.',
              '{owner} added {player} and got {points} out of him right away. The wire giveth.',
            ]),
            {
              player: facts.playerName(best.playerId),
              points: pts(best.pointsThisPeriod),
              owner: owner(facts, best.teamId),
            },
          ),
          best.teamId,
        ),
      ]
    : [line("Nobody's pickup did much this week. The wire is a long game.")];
  return { id: 'wire', title: 'The wire', lines };
}

function nextWeek(
  facts: SeasonFacts,
  p: PeriodFacts,
  next: NextPeriod | null,
  after: StandingRow[],
): RecapSection | null {
  if (!next || !next.matchups.length) return null;
  const lines: RecapLine[] = [];
  if (next.rivalry)
    lines.push(line('Next week is Rivalry Week. Plan your trash talk.', null, null, true));
  const wins = (id: string) => after.find((r) => r.teamId === id)?.wins ?? 0;
  const ranked = [...next.matchups].sort(
    (a, b) => wins(b.homeTeamId) + wins(b.awayTeamId) - (wins(a.homeTeamId) + wins(a.awayTeamId)),
  );
  const top = ranked[0]!;
  const a = after.find((r) => r.teamId === top.homeTeamId);
  const b = after.find((r) => r.teamId === top.awayTeamId);
  lines.push(
    line(
      fill(
        variant(facts, p.period, 'next', [
          'The one to watch: {aOwner} ({aRecord}) against {bOwner} ({bRecord}).',
          '{aOwner} ({aRecord}) meets {bOwner} ({bRecord}). Circle it.',
        ]),
        {
          aOwner: owner(facts, top.homeTeamId),
          bOwner: owner(facts, top.awayTeamId),
          aRecord: record(a),
          bRecord: record(b),
        },
      ),
    ),
  );
  return { id: 'next', title: `Next: Week ${next.period}`, lines };
}

// ── The recap ─────────────────────────────────────────────────────────────

/** The league recap for a final period; null when the period is not final. */
export function weeklyRecap(input: RecapInput): Recap | null {
  const { facts, period } = input;
  const p = facts.periods.find((x) => x.period === period);
  if (!p || !p.final) return null;
  const before = standingsThrough(facts, period - 1);
  const after = standingsThrough(facts, period);
  const games = classify(p, before);
  const h = headline(facts, p, games, before);
  const sections: RecapSection[] = [];
  // A week against the field has no games to tell (Q4, Q5).
  const usedLines = new Set<string>();
  if (games.length > 0)
    sections.push({
      id: 'games',
      title: 'The games',
      lines: games.map((g) => gameLine(facts, p, g, before, usedLines)),
    });
  sections.push(stars(facts, p));
  // Left out, not empty, when no bench that week was written down.
  const benchSection = bench(facts, p);
  if (benchSection.lines.length > 0) sections.push(benchSection);
  sections.push(standings(facts, p, games, before, after, input.playoffTeamCount));
  sections.push(wire(facts, p));
  const next = nextWeek(facts, p, input.next, after);
  if (next) sections.push(next);
  const candidates = candidatesFor(facts, p);
  for (const s of sections) for (const l of s.lines) l.names = namesIn(l.text, candidates);
  return { period, headline: h.headline, standfirst: h.standfirst, sections };
}

// ── Your week in one sentence ─────────────────────────────────────────────

/** One true sentence about a team's week, with the standings tag; null when the period is not final. */
export function personalLine(
  facts: SeasonFacts,
  period: number,
  teamId: string,
  playoffTeamCount: number,
): string | null {
  const p = facts.periods.find((x) => x.period === period);
  if (!p || !p.final) return null;
  const me = p.teams.find((t) => t.teamId === teamId);
  if (!me) return null;
  if (!me.played) return 'No game for you this week. Watch the others squirm.';
  const them = me.opponentTeamId ? p.teams.find((t) => t.teamId === me.opponentTeamId) : null;
  const margin = Math.round(Math.abs(me.score - (me.opponentScore ?? 0)) * 10) / 10;
  // What was left on a bench nobody wrote down is not known, and 0 here would
  // be a reason given that nobody checked.
  const myLeft =
    me.optimalPoints === null ? 0 : Math.round((me.optimalPoints - me.score) * 10) / 10;
  const theirLeft =
    them && them.optimalPoints !== null
      ? Math.round((them.optimalPoints - them.score) * 10) / 10
      : 0;
  const scores = p.teams.filter((t) => t.played).map((t) => t.score);
  const mid = median(scores);
  const oppOwner = me.opponentTeamId ? owner(facts, me.opponentTeamId) : 'them';
  const myTop = me.topStarter ? facts.playerName(me.topStarter.playerId) : null;
  const theirTop = them?.topStarter ? facts.playerName(them.topStarter.playerId) : null;
  let text: string;
  // A level score the league settled: won or lost on its rule, never "by 0.0".
  const mine = p.matchups.find((m) => m.homeTeamId === teamId || m.awayTeamId === teamId);
  const second = me.second ?? null;
  if (second !== null) {
    // A WEEK OF TWO GAMES (Q6, B-614): one score, a result against each.
    const said = (g: TeamPeriodFact) => {
      const who = g.opponentTeamId === null ? 'them' : owner(facts, g.opponentTeamId);
      const by = Math.round(Math.abs(g.score - (g.opponentScore ?? 0)) * 10) / 10;
      const how = by < 0.05 ? 'on the tiebreak' : `by ${pts(by)}`;
      return g.result === 'tie'
        ? `tied with ${who}`
        : g.result === 'win'
          ? `beat ${who} ${how}`
          : `lost to ${who} ${how}`;
    };
    const [a, b] = gamesOf(me) as [TeamPeriodFact, TeamPeriodFact];
    const wins = [a, b].filter((g) => g.result === 'win').length;
    const losses = [a, b].filter((g) => g.result === 'loss').length;
    text = `You scored ${pts(me.score)}: you ${said(a)} and ${said(b)}.${
      wins === 2 ? ' Two for two.' : losses === 2 ? ' Not your week.' : ''
    }`;
  } else if (me.result === null) {
    // A WEEK AGAINST THE FIELD (Q4, Q5): no opponent, so the week is where
    // the score put me among everybody's.
    const place = 1 + scores.filter((x) => x > me.score).length;
    text =
      place === 1
        ? `You scored ${pts(me.score)}, the top score in the league this week.`
        : `You scored ${pts(me.score)}, ${ordinal(place)} of ${scores.length} this week.`;
  } else if (me.result === 'tie')
    text = `You tied at ${pts(me.score)}. Nobody knows what to do with that.`;
  else if (mine !== undefined && mine.homeScore === mine.awayScore)
    text = `You tied at ${pts(me.score)} and ${me.result === 'win' ? 'won' : 'lost'} it ${settledOn(settled(mine).how)}.`;
  else if (me.result === 'win') {
    const share = me.topStarter && me.score > 0 ? me.topStarter.points / me.score : 0;
    if (myTop && share >= 0.3)
      text = `You won by ${pts(margin)}. ${myTop} carried you with ${pts(me.topStarter!.points)}.`;
    else if (theirLeft >= margin && theirLeft >= 10)
      text = `You won by ${pts(margin)} because ${oppOwner} left ${pts(theirLeft)} on the bench. Send a thank-you.`;
    else if (me.projected !== null && me.score - me.projected >= 10)
      text = `You won by ${pts(margin)} and beat your projection by ${pts(me.score - me.projected)}. Nobody saw it coming, including you.`;
    else if (margin < 3)
      text = `You won by ${pts(margin)}. One catch the other way and this sentence is very different.`;
    else text = `You won by ${pts(margin)}. Comfortable. Enjoy it.`;
  } else {
    if (theirTop && them?.topStarter && them.topStarter.points >= 25)
      text = `You lost by ${pts(margin)}. ${theirTop} went off for ${pts(them.topStarter.points)}; not much you can do about that.`;
    else if (myLeft >= margin && myLeft >= 5)
      text = `You lost by ${pts(margin)} and left ${pts(myLeft)} on the bench. That was the game.`;
    else if (me.score >= mid && scores.length >= 4)
      text = `You scored ${pts(me.score)}, more than most of the league, and still lost by ${pts(margin)}. Cruel.`;
    else if (margin < 3) text = `You lost by ${pts(margin)}. That's one play.`;
    else text = `You lost by ${pts(margin)}. Not your week. Next one is.`;
  }
  if (period <= facts.regularSeasonEnd) {
    const rows = standingsThrough(facts, period);
    const row = rows.find((r) => r.teamId === teamId);
    if (row && (playoffTeamCount === 0 || racesOnPoints(facts))) {
      // NO PLAYOFF LINE TO BE ON (B-362): the place, and in a race how far
      // back of the leader.
      const lead = rows[0]?.pointsFor ?? row.pointsFor;
      const back = Math.round((lead - row.pointsFor) * 10) / 10;
      text = `${text} ${
        row.rank === 1
          ? "You're first."
          : racesOnPoints(facts)
            ? `You're ${ordinal(row.rank)}, ${pts(back)} back.`
            : `You're ${ordinal(row.rank)}.`
      }`;
    } else if (row) {
      const inside = row.rank <= playoffTeamCount;
      const line = inside
        ? row.rank === 1
          ? "You're first."
          : `You're ${ordinal(row.rank)}, inside the playoff line.`
        : (() => {
            const last = rows[playoffTeamCount - 1];
            const gap = last ? last.wins - row.wins : 0;
            return gap <= 0
              ? `You're ${ordinal(row.rank)}, outside the playoff line on points.`
              : `You're ${ordinal(row.rank)}, ${gap} ${gap === 1 ? 'game' : 'games'} outside the playoff line.`;
          })();
      text = `${text} ${line}`;
    }
  }
  return text;
}
